import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Group,
  LoadingOverlay,
  Stack,
  TextInput,
  ActionIcon,
  Title,
  Breadcrumbs,
  Anchor,
  Card,
  ScrollArea,
  Box,
  Container,
} from "@mantine/core";
import { Link, useLocation } from "react-router-dom";
import { createFormContext } from "@mantine/form";
import { CirclePlus, Copy, Trash } from "tabler-icons-react";
import { ADMIN_PATH } from "_urls";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";

// ----- Types -----
export type Player = {
  id: string;
  name: string;
  logo: string;
  status: "enabled" | "disabled";
  index?: number;
};

export type WorkforceModel = {
  id: string;
  organization?: string;
  name: string;
  players: Player[];
};

// ----- Zod Schemas -----
const playerSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Player name is required"),
  logo: z.string().min(1, "Photo URL is required"),
  status: z.enum(["enabled", "disabled"]),
  index: z.number().optional(),
});

const workforceModelSchema = z.object({
  id: z.string(),
  organization: z.string().optional(),
  name: z.string().optional(),
  players: z.array(playerSchema),
});

// ----- Create form context -----
export const [
  WorkforceFormProvider,
  useWorkforceFormContext,
  useWorkforceForm,
] = createFormContext<WorkforceModel>();

// ----- The form component -----
export function StaffAdditionForm({
  workforce,
  editMode,
  onSaveLocally,
  setOpened,
}: {
  workforce?: WorkforceModel;
  editMode: boolean;
  onSaveLocally?: (data: WorkforceModel) => void;
  setOpened: (opened: boolean) => void;
}) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isDuplicate = location.pathname.endsWith("/duplicate");

  // Provide initial form values based on workforce and edit mode
  const getInitialValues = useCallback(() => {
    if (workforce && editMode) {
      return {
        id: isDuplicate ? "" : workforce.id,
        organization: workforce.organization || "",
        name: workforce.name || "",
        players:
          workforce.players?.map((player, i) => ({
            ...player,
            id: player.id || uuidv4(),
            status: player.status || "enabled",
            index: i + 1,
          })) || [],
      };
    }
    return {
      id: "",
      organization: "",
      name: "",
      players: [],
    };
  }, [workforce, editMode, isDuplicate]);

  // Mantine form setup with Zod validation
  const form = useWorkforceForm({
    initialValues: getInitialValues(),
    validate: (values) => {
      const result = workforceModelSchema.safeParse(values);
      if (!result.success) {
        return result.error.formErrors.fieldErrors;
      }
      return {};
    },
  });

  const isDirty = useMemo(
    () => JSON.stringify(form.values) !== JSON.stringify(getInitialValues()),
    [form.values, getInitialValues]
  );

  // Add new empty player if last one is valid
  const handleAddPlayer = () => {
    const current = form.values.players;
    if (
      current.length > 0 &&
      (!current.at(-1)?.name?.trim() ||
        !current.at(-1)?.logo?.trim() ||
        !current.at(-1)?.status)
    ) {
      return;
    }

    const newPlayers = [
      ...current,
      { id: uuidv4(), name: "", logo: "", status: "enabled" },
    ];

    form.setFieldValue(
      "players",
      newPlayers.map((player, i) => ({
        ...player,
        index: i + 1,
        status: player.status as "enabled" | "disabled",
      }))
    );
  };

  // Duplicate player by index
  const handleDuplicatePlayer = (indexToDuplicate: number) => {
    const player = form.values.players[indexToDuplicate];
    if (!player?.name?.trim() || !player?.logo?.trim() || !player?.status) {
      return;
    }

    const duplicated = {
      ...player,
      id: uuidv4(),
      index: form.values.players.length + 1,
      name: `${player.name} (Copy)`,
    };

    const newPlayers = [...form.values.players];
    newPlayers.splice(indexToDuplicate + 1, 0, duplicated);

    const reIndexed = newPlayers.map((player, i) => ({
      ...player,
      index: i + 1,
    }));
    form.setFieldValue("players", reIndexed);
  };

  // Remove player by index
  const handleRemovePlayer = (indexToRemove: number) => {
    const updated = form.values.players
      .filter((_, i) => i !== indexToRemove)
      .map((player, i) => ({ ...player, index: i + 1 }));

    form.setFieldValue("players", updated);
  };

  // Save form values
  const save = async () => {
    const validationResult = workforceModelSchema.safeParse(form.values);
    if (validationResult.success) {
      setIsLoading(true);
      try {
        if (onSaveLocally) {
          onSaveLocally(form.values);
        }
        setOpened(false);
      } catch (error) {
        console.error("Error saving form locally", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Convert Zod errors to Mantine form errors
      const fieldErrors = validationResult.error.formErrors.fieldErrors;
      Object.entries(fieldErrors).forEach(([field, messages]) => {
        form.setFieldError(field, messages?.join(", "));
      });
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay visible overlayOpacity={0.8} />}
      <WorkforceFormProvider form={form}>
        <Box
          sx={(theme) => ({
            background:
              theme.colorScheme === "dark"
                ? theme.colors.gray[8]
                : theme.colors.gray[2],
            minHeight: "calc(100vh - 64px)",
          })}
          p={"lg"}
        >
          <Container className="min-h-full">
            <div
              className="sticky top-0 z-50 pb-4"
              style={{ backgroundColor: "#E9ECEF" }}
            >
              <Title order={3} align="center" m="xs">
                {form.values.name || "New Workforce"}
              </Title>
              <Breadcrumbs separator="→">
                <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                  Admin
                </Anchor>
                <Anchor component={Link} to={`/${ADMIN_PATH}/pronosticsv2`}>
                  Pronostics
                </Anchor>
                <Anchor onClick={() => setOpened(false)}>Workforce Form</Anchor>
              </Breadcrumbs>
              <Group position="right">
                <Button
                  disabled={!isDirty}
                  onClick={save}
                  className="bg-button-admin disabled:opacity-75"
                >
                  Ok
                </Button>
              </Group>
            </div>
            <ScrollArea h="calc(100vh - 145px)" type="never">
              {/* <Group className="grid grid-cols-2 gap-4">
                <TextInput
                  label="Organization"
                  {...form.getInputProps("organization")}
                />
                <TextInput
                  label="Workforce Name"
                  required
                  {...form.getInputProps("name")}
                />
              </Group> */}

              {form.values.players.map((player, index) => (
                <div key={index}>
                  <Stack spacing="xs" mb="md" py={"xs"}>
                    <Group position="apart" align="center">
                      <Title order={5}>Player #{index + 1}</Title>
                      <Group spacing="xs">
                        <ActionIcon
                          onClick={() => handleDuplicatePlayer(index)}
                          disabled={
                            !player.name?.trim() ||
                            !player.logo?.trim() ||
                            !player.status
                          }
                        >
                          <Copy size={20} color="blue" />
                        </ActionIcon>
                        <ActionIcon onClick={() => handleRemovePlayer(index)}>
                          <Trash color="red" size={20} />
                        </ActionIcon>
                      </Group>
                    </Group>
                    <Card shadow="sm" p="xl" className="overflow-visible">
                      <Group className="grid grid-cols-1 gap-4">
                        <TextInput
                          label="Player Name"
                          required
                          {...form.getInputProps(`players.${index}.name`)}
                        />
                        <TextInput
                          label="Photo URL"
                          required
                          {...form.getInputProps(`players.${index}.logo`)}
                        />
                      </Group>
                      {/* <TextInput
                        label="Player ID"
                        className="mt-4"
                        disabled
                        {...form.getInputProps(`players.${index}.id`)}
                      /> */}

                      {/* <Radio.Group
                        label="Status"
                        className="mt-4"
                        {...form.getInputProps(`players.${index}.status`)}
                      >
                        <Group mt="xs">
                          <Radio value="enabled" label="Enabled" />
                          <Radio value="disabled" label="Disabled" />
                        </Group>
                      </Radio.Group> */}
                    </Card>
                  </Stack>
                </div>
              ))}

              <Group position="center">
                <Button
                  onClick={handleAddPlayer}
                  variant="subtle"
                  leftIcon={<CirclePlus />}
                  disabled={
                    form.values.players.length > 0 &&
                    (!form.values.players.at(-1)?.name?.trim() ||
                      !form.values.players.at(-1)?.logo?.trim() ||
                      !form.values.players.at(-1)?.status)
                  }
                >
                  Add Player
                </Button>
              </Group>
            </ScrollArea>
          </Container>
        </Box>
      </WorkforceFormProvider>
    </>
  );
}
